/**
 * ReactivityModel — the pure envelope pool + anti-strobe gates behind the
 * world-combat reactivity bus (docs/plans/fx-grammar-specs/world-combat-reactivity.md
 * §2.3/§2.5). No I/O, no events, no Pixi — every method takes an explicit
 * `now` (ms), so the whole model is unit-testable like CombatMoodModel.
 *
 * Envelope (the executable invariant — see ReactivityModel.test.ts):
 *
 *   factor(t) = 1 + amp × attackRamp(t / attackMs)                 t < attackMs
 *             = 1 + amp × exp(-(t - attackMs) / decayMs)           t ≥ attackMs
 *
 * The factor starts at 1, peaks at 1 + amp, and always decays back to 1 —
 * a dead envelope (t > attackMs + 4 × decayMs) frees its fixed pool slot and
 * contributes exactly 1. There is NO code path that writes a persistent value.
 *
 * Three bounded gates (spec §2.5, the anti-strobe contract):
 *  1. budget — Σ|amp| of live envelopes capped; over-budget pulses scale down
 *     to the remainder (never suddenly inert), floored then dropped.
 *  2. per-pulse-kind cooldown — pulses inside the window MERGE into the live
 *     envelope (decay refreshed at max-magnitude amp) — sustained fights read
 *     as sustained elevation, not machine-gun flashes.
 *  3. flash-rate ceiling — attack onsets globally capped per second; excess
 *     pulses skip their attack and splice straight into decay.
 */
import {
  REACTIVITY,
  type ReactivityConfig,
  type ReactivityPulseKind,
  type ReactivityResponse,
} from '../../../config/atmosphere/reactivity.config';

/** Scalar channels evaluated per frame in tickModulation. */
export type ScalarChannel = 'opacity' | 'density' | 'speed' | 'tint';

export interface PulseInput {
  kind: ReactivityPulseKind;
  school?: string | null;
  /** 0..1 (clamped) — scales every response amp. */
  intensity: number;
  hasPosition: boolean;
}

export interface PulseVerdictAccepted {
  accepted: true;
  /** Cooldown merge — decay refreshed on live envelopes, no new onset, no
   *  positional fan-out. */
  merged: boolean;
  /** < 1 when the budget gate scaled the incoming amps down. */
  budgetScale: number;
  /** Flash ceiling hit — envelopes spliced into decay (no attack ramp). */
  attackSkipped: boolean;
  /** Positional responses to fan out NOW (empty on merge / position-less pulses). */
  positional: ReactivityResponse[];
}

export interface PulseVerdictDropped {
  accepted: false;
  dropped: 'disabled' | 'no-responses' | 'budget' | 'pool';
}

export type PulseVerdict = PulseVerdictAccepted | PulseVerdictDropped;

/** One fixed pool slot — plain mutable fields, freed by deactivation. */
interface EnvelopeSlot {
  active: boolean;
  pulseKind: ReactivityPulseKind;
  school: string | null;
  layerKind: string;
  channel: ScalarChannel;
  /** Effective amp (response amp × intensity × budget scale). May be negative. */
  amp: number;
  /** 0 when the attack was spliced away (flash ceiling). */
  attackMs: number;
  decayMs: number;
  startAt: number;
}

/** Fast attack ramp — rises quickly, settles at the peak (quad ease-out). */
function attackRamp(u: number): number {
  if (u <= 0) return 0;
  if (u >= 1) return 1;
  return u * (2 - u);
}

const ONSET_WINDOW_MS = 1000;

export class ReactivityModel {
  private readonly slots: EnvelopeSlot[];
  private readonly lastAcceptedAt = new Map<ReactivityPulseKind, number>();
  private onsetTimes: number[] = [];

  constructor(private readonly cfg: ReactivityConfig = REACTIVITY) {
    this.slots = Array.from({ length: cfg.poolSize }, () => ({
      active: false, pulseKind: 'bigCast' as ReactivityPulseKind, school: null,
      layerKind: '', channel: 'opacity' as ScalarChannel, amp: 0, attackMs: 0, decayMs: 1, startAt: 0,
    }));
  }

  /** Gate + pool a pulse's responses. Pure state transition — no I/O. */
  push(pulse: PulseInput, responses: ReactivityResponse[], now: number): PulseVerdict {
    if (!this.cfg.enabled) return { accepted: false, dropped: 'disabled' };
    const intensity = Math.max(0, Math.min(1, pulse.intensity));
    const scalars = responses.filter((r) => r.channel !== 'positional');
    const positional = pulse.hasPosition ? responses.filter((r) => r.channel === 'positional') : [];
    if (scalars.length === 0 && positional.length === 0) return { accepted: false, dropped: 'no-responses' };

    // ── Gate 2: per-pulse-kind cooldown → merge (refresh, don't stack) ──
    const last = this.lastAcceptedAt.get(pulse.kind);
    const cooldown = this.cfg.cooldownMs[pulse.kind] ?? 250;
    if (last !== undefined && now - last < cooldown) {
      for (const r of scalars) {
        const amp = r.amp * intensity;
        for (const s of this.slots) {
          if (!s.active || s.pulseKind !== pulse.kind || s.layerKind !== r.kind || s.channel !== r.channel) continue;
          if (!this.isLive(s, now)) continue;
          // Refresh: keep the larger-magnitude amp, restart the decay at the
          // peak (t rewound to the attack end — sustained elevation).
          if (Math.abs(amp) > Math.abs(s.amp)) s.amp = amp;
          s.startAt = now - s.attackMs;
        }
      }
      return { accepted: true, merged: true, budgetScale: 1, attackSkipped: false, positional: [] };
    }

    // ── Gate 1: global amp budget (scalar envelopes only) ──
    let budgetScale = 1;
    if (scalars.length > 0) {
      let liveSum = 0;
      for (const s of this.slots) {
        if (s.active && this.isLive(s, now)) liveSum += Math.abs(s.amp);
      }
      let incoming = 0;
      for (const r of scalars) incoming += Math.abs(r.amp) * intensity;
      const remaining = this.cfg.budgetAmpSum - liveSum;
      if (incoming > remaining) {
        if (remaining < this.cfg.budgetFloor) return { accepted: false, dropped: 'budget' };
        budgetScale = remaining / incoming;
      }
    }

    // ── Gate 3: flash-rate ceiling — skip the attack when onsets saturate ──
    this.onsetTimes = this.onsetTimes.filter((t) => now - t < ONSET_WINDOW_MS);
    const attackSkipped = scalars.length > 0 && this.onsetTimes.length >= this.cfg.maxOnsetsPerSec;
    if (scalars.length > 0 && !attackSkipped) this.onsetTimes.push(now);

    // ── Allocate fixed pool slots ──
    let landed = 0;
    for (const r of scalars) {
      const slot = this.freeSlot(now);
      if (!slot) continue; // pool saturated — skip this response
      slot.active = true;
      slot.pulseKind = pulse.kind;
      slot.school = pulse.school ?? null;
      slot.layerKind = r.kind;
      slot.channel = r.channel as ScalarChannel;
      slot.amp = r.amp * intensity * budgetScale;
      slot.attackMs = attackSkipped ? 0 : (r.attackMs ?? this.cfg.defaultAttackMs);
      slot.decayMs = Math.max(1, r.decayMs);
      slot.startAt = now;
      landed += 1;
    }
    if (scalars.length > 0 && landed === 0 && positional.length === 0) {
      return { accepted: false, dropped: 'pool' };
    }

    this.lastAcceptedAt.set(pulse.kind, now);
    return { accepted: true, merged: false, budgetScale, attackSkipped, positional };
  }

  /** Composed multiplicative factor for a (layer kind, channel) at `now`.
   *  Live envelopes multiply; the product is clamped to
   *  [minCompoundFactor, maxCompoundFactor]. 1 when nothing is live. */
  factorFor(layerKind: string, channel: ScalarChannel, now: number): number {
    let product = 1;
    let any = false;
    for (const s of this.slots) {
      if (!s.active || s.layerKind !== layerKind || s.channel !== channel) continue;
      if (!this.isLive(s, now)) { s.active = false; continue; } // free dead slots lazily
      product *= this.slotFactor(s, now);
      any = true;
    }
    if (!any) return 1;
    return Math.max(this.cfg.minCompoundFactor, Math.min(this.cfg.maxCompoundFactor, product));
  }

  /** Cheap per-frame guard — true when any envelope is live. */
  hasActive(now: number): boolean {
    for (const s of this.slots) {
      if (s.active && this.isLive(s, now)) return true;
    }
    return false;
  }

  /** Debug/VQA readout (`__atmosphere.getActivePulses()`). */
  activePulses(now: number): Array<{ kind: string; school: string | null; layerKind: string; channel: ScalarChannel; factor: number }> {
    const out: Array<{ kind: string; school: string | null; layerKind: string; channel: ScalarChannel; factor: number }> = [];
    for (const s of this.slots) {
      if (!s.active || !this.isLive(s, now)) continue;
      out.push({ kind: s.pulseKind, school: s.school, layerKind: s.layerKind, channel: s.channel, factor: this.slotFactor(s, now) });
    }
    return out;
  }

  /** Full teardown (tests / disconnect). */
  reset(): void {
    for (const s of this.slots) s.active = false;
    this.lastAcceptedAt.clear();
    this.onsetTimes = [];
  }

  // ── internals ─────────────────────────────────────────────────────

  private slotFactor(s: EnvelopeSlot, now: number): number {
    const t = now - s.startAt;
    if (t < 0) return 1;
    // attackMs 0 (spliced) falls straight to the decay branch: peak at t=0.
    if (t < s.attackMs) return 1 + s.amp * attackRamp(t / s.attackMs);
    return 1 + s.amp * Math.exp(-(t - s.attackMs) / s.decayMs);
  }

  private isLive(s: EnvelopeSlot, now: number): boolean {
    return now - s.startAt <= s.attackMs + 4 * s.decayMs;
  }

  private freeSlot(now: number): EnvelopeSlot | null {
    for (const s of this.slots) {
      if (!s.active) return s;
      if (!this.isLive(s, now)) { s.active = false; return s; }
    }
    return null;
  }
}
